import React, { useState } from 'react';
import './IssuanceFormScreen.css';
import { useNavigate } from 'react-router-dom';
import { CredentialUrls } from '../../dtos/credentials';
import { initOpenIdIssuance, IssuanceData } from '../../services/vidcredentials';

interface InitOpenIdPresentationResponse {
  qrBase64: string;
  rawOpenid4vp: string;
  userPin?: number
}

function resizeImage(
  file: File,
  maxWidth: number,
  maxHeight: number,
): Promise<Blob> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');

      const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
      const width = img.width * scale;
      const height = img.height * scale;

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);

      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
      }, 'image/jpeg', 0.7);
    };
    img.src = URL.createObjectURL(file);
  });
}

export default function IssuanceFormScreen() {
  const [qrCode, setQrCode] = useState('');
  const [pinCode, setPinCode] = useState<number>(0);
  const [username, setUsername] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [photo, setPhoto] = useState<string>('');
  const navigate = useNavigate();
  const generateQr = async (formData: IssuanceData) => {
    const retryLocation = CredentialUrls.VALIDATE;
    const response = await
    initOpenIdIssuance('pre-auth', formData);
    if (!!response && 'error' in response) {
      navigate('/validation-process/failure', { state: { retryLocation } });
      return;
    }
    const initOpenIdPresentationResponse:
    InitOpenIdPresentationResponse = response.data;
    setQrCode(initOpenIdPresentationResponse.qrBase64);
    if (initOpenIdPresentationResponse.userPin) {
      setPinCode(initOpenIdPresentationResponse.userPin);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formData: IssuanceData = {
      name: username,
      surname,
      email,
      photo,
    };

    generateQr(formData);
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const resizedBlob = await resizeImage(file, 200, 200);
    const reader = new FileReader();

    reader.onloadend = () => {
      const base64String = reader.result as string;
      setPhoto(base64String);
    };

    reader.readAsDataURL(resizedBlob);
  };

  return (
    <div>
      <img
        src="/genericBackground.svg"
        alt="genericBackground"
        style={{
          position: 'absolute', top: 0, left: 0, objectFit: 'cover', width: '100vw', height: '100vh', zIndex: -1,
        }}
      />

      <nav className="navigation">
        {/* Logo aligned left */}
        <img src="/whiteLogo.svg" alt="DigitalGov Logo" className="logoWhite" />

        <div className="nav-wrapper">
          <a href="/" className="nav-link nav-link-active">Home</a>
          <span className="arrow">›</span>
          <span className="nav-link">About</span>
          <span className="arrow">›</span>
          <span className="nav-link">Features</span>
          <span className="arrow">›</span>
          <span className="nav-link">Services</span>

          <div className="nav-spacer" />

          <div className="logout-text">
            <a href="/">
              Log out
              {' '}
              <img src="/userLogo.svg" alt="userLogo" className="user-icon" />
            </a>
          </div>
        </div>
      </nav>

      {/* Greeting */}
      <section className="greeting-validation">
        <h1>
          Hello again,
          {' '}
          Admin
        </h1>
      </section>

      {/* Main Layout */}
      {/* Main Container */}
      <div className="main-container">
        {/* Left Sidebar */}
        <div className="sidebar">
          <div className="card-top">
            <div className="credential-icon" />
            <img src="/cardImage.svg" alt="Card" className="card-image" />
          </div>
        </div>

        {/* Main Content */}
        <div className="content">
          {!qrCode ? (
            <>
              <h3 className="form">Get your personal credential</h3>
              <hr className="form-divider" />
              <form onSubmit={handleSubmit} className="form">
                <div className="form-group">
                  {/* eslint-disable-next-line
                   jsx-a11y/label-has-associated-control */}
                  <label htmlFor="username">Username</label>
                  <input
                    id="username"
                    placeholder="Enter user name"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  {/* eslint-disable-next-line
                   jsx-a11y/label-has-associated-control */}
                  <label htmlFor="surname">Surname</label>
                  <input
                    id="surname"
                    placeholder="Enter surname"
                    value={surname}
                    onChange={(e) => setSurname(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  {/* eslint-disable-next-line
                   jsx-a11y/label-has-associated-control */}
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    type="email"
                    placeholder="Enter address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  {/* eslint-disable-next-line
                  jsx-a11y/label-has-associated-control */}
                  <label htmlFor="photo">Photo</label>
                  <input
                    id="photo"
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                  />
                  {photo && (
                  <div className="photo-preview">
                    <p>Preview:</p>
                    <img src={photo} alt="Preview" style={{ maxWidth: '50px', marginTop: '10px' }} />
                  </div>
                  )}
                </div>

                <div className="form-buttons">
                  <button type="button" className="cancel" onClick={() => navigate('/login')}>Cancel</button>
                  <button type="submit" className="submit">Next</button>
                </div>
              </form>
            </>
          ) : (
            <div>
              <h3 className="form-title">Open your wallet and scan the QR Code</h3>
              <hr className="form-divider-card" />
              <button type="button" className="back-button" onClick={() => navigate('/login')}>
                <img src="/goBack.svg" alt="Back Logo" />
                Back
              </button>
              <div className="right-panel-form">
                <div className="left-panel-bottom" />
                <div className="overlay-card">
                  <div className="qr-box">
                    <img src={qrCode} alt="QR Code" className="qr-code" />
                    <div style={{
                      marginTop: '20px', textAlign: 'center', fontSize: '18px', fontWeight: 'bold', color: 'black',
                    }}
                    >
                      PIN Code:
                      {' '}
                      {pinCode}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
